/**
 * Générateur — Il résout des problèmes avec des puissances, notamment en
 * utilisant la notation scientifique.
 * Attendu ID : 204 | Niveau : 3ème | Domaine : Nombres (d11, O4)
 *
 * Source doc prof (NOMBRE ET CALCULS 3e, séances A4S3 + Créés n°3-4). Plan de
 * cartes auto-validé 2026-07-16 (journal TODO_EXERCISEUR_3E.md §6) :
 *
 *   « notation »     — écrire en notation scientifique, entrées non
 *                      normalisées incluses (2 trous mantisse/exposant)
 *   « calcul »       — produit/quotient → notation scientifique (QCM)
 *   « stockage »     — octets To/Go/Mo (nombre de fichiers, 1 trou)
 *   « sciences »     — temps lumière / masse d'un atome (VERSION RÉFÉRENTIEL
 *                      Créé n°3 — l'Ex.7 du livre à coquille est écarté)
 *   « bacteries »    — population qui double tous les quarts d'heure
 *   « enigme »       — 4ˣ = 8ᵐ : retour à la base commune (1 trou)
 *   « binaire »      — binaire ↔ décimal (trous)
 *   « denombrement » — cadenas 10ⁿ / containers en puissance de 2
 *   « balle »        — la balle qui rebondit ((3/4)ⁿ, hauteurs entières)
 *
 * Développement décimal EXACT par chaînes (nombres.js). Exposants Unicode à
 * l'affichage, saisies ASCII signées.
 */

import {
    randomChoice, randInt, shuffle, tex,
    makeQcm, makeTexteTrou,
} from '../../../utils';
import { puissFrag, fracFrag, decFrag, developpe, grp, pow } from './nombres';

const ATTENDU_ID = 204;

const L_NOTATION = 'Écrire un nombre en notation scientifique';
const L_CALCUL = 'Calculer en notation scientifique';
const L_STOCKAGE = 'Résoudre un problème de stockage (octets)';
const L_SCIENCES = 'Puissances de 10 en sciences';
const L_BACTERIES = 'Modéliser une croissance qui double';
const L_ENIGME = 'Résoudre une énigme de puissances';
const L_BINAIRE = 'Passer du binaire au décimal (et retour)';
const L_DENOMBREMENT = 'Dénombrer avec des puissances';
const L_BALLE = 'La balle qui rebondit (puissances de fractions)';

const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Mantisse normalisée aléatoire (1 ≤ m < 10), 1-3 chiffres significatifs.
const mantisseNormale = () => {
    const ent = randInt(1, 9);
    const nd = randInt(0, 2);
    let dec = '';
    for (let i = 0; i < nd; i++) dec += String(randInt(0, 9));
    dec = dec.replace(/0+$/, '');
    return dec ? `${ent},${dec}` : String(ent);
};

// Notation scientifique EXACTE d'un entier (chaîne de chiffres).
const sciDeEntier = (nStr) => {
    const digits = String(nStr).replace(/\s/g, '');
    const md = digits.replace(/0+$/, '');
    const mant = md.length > 1 ? `${md[0]},${md.slice(1)}` : md;
    return { mant, e: digits.length - 1 };
};

// ═════════ « notation » — 2 trous mantisse/exposant ═════════

const genNotationTrous = () => {
    const mant = mantisseNormale();
    const e = randInt(-6, 9);
    const forme = randomChoice(['plain', 'plain', 'nonNorm']);
    let affiche;
    if (forme === 'plain') {
        // Les séparateurs de milliers restent en TEXTE : \, dans du KaTeX ne
        // survivrait pas à la lecture d'un élève qui recopie le nombre.
        affiche = decFrag(grp(developpe(mant, e)).replace(/ | /g, '\\,'));
    } else {
        // Écriture « m × 10^k » NON normalisée (mantisse décalée de s rangs).
        let s;
        do { s = randInt(-2, 2); } while (s === 0);
        affiche = `${decFrag(grp(developpe(mant, s)).replace(/ | /g, '\\,'))} \\times ${puissFrag(10, e - s)}`;
    }
    const phrase = `$${affiche} = {{${mant}}} \\times 10^{{{${e}}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On veut la notation scientifique ${tex('a \\times 10^{n}')}, où a est un nombre `
        // `\lt` et pas `<` : on ne laisse jamais un chevron brut dans une chaîne HTML.
        + `tel que ${tex('1 \\leqslant a \\lt 10')}.</p>`
        + '<p><b>Complète la mantisse a et l\'exposant n.</b> Écris la mantisse avec une '
        + 'virgule et l\'exposant avec son signe.</p>',
        { ...opts(L_NOTATION), mode: 'saisie' });
    payload._meta.params = { mant, e, forme, affiche };
    return payload;
};

// ═════════ « calcul » — produit/quotient en notation scientifique (QCM) ═════════

const genCalculQcm = () => {
    // (a×10^p) × (b×10^q) ÷ (c×10^r) avec (a·b)/c entier à 1 chiffre non nul
    // suivi éventuellement d'un zéro → mantisse propre après normalisation.
    const TRIPLETS = [
        [6, 35, 14], [4, 15, 12], [8, 15, 6], [9, 14, 21], [6, 25, 15],
        [8, 21, 12], [45, 4, 18], [49, 27, 63], [35, 6, 21],
    ];
    const [a, b, c] = randomChoice(TRIPLETS);
    const m0 = (a * b) / c;                        // entier par construction
    const { mant, e: shift } = sciDeEntier(String(m0));
    const p = randInt(2, 12); const q = randInt(-8, 8); const r = randInt(1, 10);
    const eFinal = p + q - r + shift;
    const sci = (m, ex) => tex(`${decFrag(m)} \\times ${puissFrag(10, ex)}`);
    const correct = sci(mant, eFinal);
    const cand = new Set([correct]);
    cand.add(sci(mant, p + q + r + shift));            // on additionne r (écart 2r ≠ 0)
    if (shift > 0) cand.add(sci(m0, p + q - r));       // mantisse non normalisée (même valeur)
    // Décalage d'exposant : ±2 en évitant la collision avec le distracteur « +r » (r = 1).
    cand.add(sci(mant, r === 1 ? eFinal - 2 : eFinal + 2));
    const distracteurs = [...cand].filter(x => x !== correct);
    const payload = makeQcm([correct, ...shuffle(distracteurs).slice(0, 3)], correct,
        `<p>On pose ${tex(`B = \\dfrac{${a} \\times ${puissFrag(10, p)} \\times ${b} \\times ${puissFrag(10, q)}}{${c} \\times ${puissFrag(10, r)}}`)}.</p>`
        + '<p><b>Quelle est l\'écriture scientifique de B ?</b></p>',
        opts(L_CALCUL));
    payload._meta.params = { a, b, c, p, q, r, mant, eFinal };
    return payload;
};

// ═════════ « stockage » — octets (1 trou) ═════════

const genStockageTrous = () => {
    const variante = randomChoice(['videos', 'photos']);
    let texte; let nb;
    const mot = variante === 'videos' ? 'vidéos' : 'photos';
    if (variante === 'videos') {
        const T = randomChoice([1, 2, 4]);               // disque en To = T × 10¹² octets
        const v = randomChoice([2, 4, 5, 8, 10, 16, 20, 25].filter(x => (1000 * T) % x === 0));
        nb = (1000 * T) / v;
        texte = `<p>Un disque dur a une capacité de ${T} To, soit ${tex(`${T} \\times ${puissFrag(10, 12)}`)} octets. `
            + `Chaque vidéo occupe ${v} Go, soit ${tex(`${v} \\times ${puissFrag(10, 9)}`)} octets.</p>`
            + '<p><b>Combien de vidéos peut-on stocker au maximum ?</b></p>';
    } else {
        const G = randomChoice([1, 2, 4, 8]);            // clé en Go
        const m = randomChoice([2, 4, 5, 8, 10, 20, 25].filter(x => (1000 * G) % x === 0));
        nb = (1000 * G) / m;
        texte = `<p>Une clé USB a une capacité de ${G} Go, soit ${tex(`${G} \\times ${puissFrag(10, 9)}`)} octets. `
            + `Chaque photo occupe ${m} Mo, soit ${tex(`${m} \\times ${puissFrag(10, 6)}`)} octets.</p>`
            + '<p><b>Combien de photos peut-on stocker au maximum ?</b></p>';
    }
    const phrase = `On peut stocker {{${nb}}} ${mot}.`;
    const payload = makeTexteTrou(phrase, `${texte}<p>Écris un entier, sans espaces.</p>`,
        { ...opts(L_STOCKAGE), mode: 'saisie' });
    payload._meta.params = { variante, nb };
    return payload;
};

// ═════════ « sciences » — temps lumière / masse d'un atome ═════════

const genSciencesTrous = () => {
    if (randInt(0, 1)) {
        // Temps de parcours de la lumière : d = 3×10⁵ km/s × t (t construit).
        // d = t × 3×10⁵ km = (3t/10) × 10⁶ km — division par 10 EXACTE par chaîne.
        const t = randInt(80, 600);
        const dAff = developpe(String(3 * t), -1);
        const phrase = `t = {{${t}}} secondes.`;
        const payload = makeTexteTrou(phrase,
            `<p>Une planète est à ${tex(`${decFrag(dAff)} \\times ${puissFrag(10, 6)}`)} km de son étoile. `
            + `La lumière parcourt ${tex(`3 \\times ${puissFrag(10, 5)}`)} km par seconde.</p>`
            + '<p><b>Combien de temps met la lumière pour arriver ?</b> Donne un nombre entier de secondes.</p>',
            { ...opts(L_SCIENCES), mode: 'saisie' });
        payload._meta.params = { famille: 'lumiere', t, dAff };
        return payload;
    }
    // Masse d'un atome (version RÉFÉRENTIEL, Créé n°3) : m ng contiennent
    // x × 10¹⁵ atomes → masse = (m/x) × 10⁻²⁴ g, m = c·x pour un quotient propre.
    const x = randomChoice([2, 4, 5]);
    const c = randInt(1, 9);
    const m = c * x;                                   // masse en ng (nanogrammes)
    const metal = randomChoice(['cuivre', 'fer', 'zinc', 'argent']);
    const deMetal = /^[aeéiou]/.test(metal) ? `d'${metal}` : `de ${metal}`;   // élision
    const phrase = `$\\text{1 ng} = 10^{{{-9}}}\\ \\text{g} \\qquad`
        + ` \\text{masse d'un atome} \\approx {{${c}}} \\times 10^{{{-24}}}\\ \\text{g}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Il y a environ ${tex(`${x} \\times ${puissFrag(10, 15)}`)} atomes ${deMetal} dans ${m} ng ${deMetal}.</p>`
        + '<p><b>Rappelle ce que vaut 1 ng en gramme, puis donne la masse approximative '
        + 'd\'un atome.</b> Écris les exposants avec leur signe.</p>',
        { ...opts(L_SCIENCES), mode: 'saisie' });
    payload._meta.params = { famille: 'atome', x, c, m };
    return payload;
};

// ═════════ « bacteries » — doublement tous les quarts d'heure ═════════

const genBacteriesTrous = () => {
    const n0 = randomChoice([1000, 1500, 2000, 2500, 3000, 4000, 5000]);
    const heures = randomChoice([1, 2, 3]);
    const k = heures * 4;                              // doublements
    const N = n0 * pow(2, k);
    const { mant, e } = sciDeEntier(String(N));
    const phrase = `$\\text{Doublements : } {{${k}}} \\qquad \\text{Population : }`
        + ` {{${mant}}} \\times 10^{{{${e}}}}\\ \\text{bactéries}$`;
    const payload = makeTexteTrou(phrase,
        `<p>À 37 °C, une population de bactéries double tous les quarts d'heure. `
        + `On part de ${grp(String(n0))} bactéries.</p>`
        + `<p><b>Combien de doublements ont lieu en ${heures === 1 ? 'une heure' : `${heures} heures`}, `
        + 'et quelle est alors la population en notation scientifique ?</b></p>',
        { ...opts(L_BACTERIES), mode: 'saisie' });
    payload._meta.params = { n0, heures, k, N, mant, e };
    return payload;
};

// ═════════ « enigme » — 4ˣ = 8ᵐ (1 trou) ═════════

const genEnigmeTrous = () => {
    // Couples (A, B) de même base première b : A = b^i, B = b^j.
    const FAMILLES = [
        { A: 4, B: 8, i: 2, j: 3 }, { A: 8, B: 4, i: 3, j: 2 },
        { A: 9, B: 27, i: 2, j: 3 }, { A: 27, B: 9, i: 3, j: 2 },
        { A: 25, B: 125, i: 2, j: 3 }, { A: 4, B: 16, i: 2, j: 4 },
    ];
    const { A, B, i, j } = randomChoice(FAMILLES);
    // A^x = B^m ⟺ i·x = j·m : choisir x entier ≥ 2 et m = i·x/j entier.
    let x; let m;
    for (let essai = 0; essai < 30; essai++) {
        x = randInt(2, 12);
        if ((i * x) % j === 0) { m = (i * x) / j; if (m >= 2 && m <= 24) break; }
        m = null;
    }
    if (!m) { x = j; m = i; }                          // repli : A^j = B^i toujours vrai
    const phrase = `Il faut élever ${A} à la puissance {{${x}}}.`;
    const payload = makeTexteTrou(phrase,
        // L'indice « A et B sont des puissances d'un même nombre » donnait la
        // méthode entière : c'est précisément ce que l'énigme demande de voir.
        `<p><b>À quelle puissance faut-il élever ${A} pour obtenir ${tex(puissFrag(B, m))} ?</b></p>`,
        { ...opts(L_ENIGME), mode: 'saisie' });
    payload._meta.params = { A, B, m, x };
    return payload;
};

// ═════════ « binaire » — binaire ↔ décimal ═════════

const genBinaireTrous = () => {
    const sens = randomChoice(['versDecimal', 'versBinaire']);
    const exemple = tex('1101_{2} = 1 \\times 2^{3} + 1 \\times 2^{2} + 0 \\times 2^{1} + 1 \\times 2^{0} = 13');
    if (sens === 'versDecimal') {
        let v;
        do { v = randInt(9, 255); } while (v === 13);
        const bin = v.toString(2);
        // « (binaire) » / « (décimal) » sont des ÉTIQUETTES de base, pas du
        // commentaire : elles passent en indice, la notation du programme.
        const phrase = `$${bin}_{2} = {{${v}}}$`;
        const payload = makeTexteTrou(phrase,
            `<p>En binaire, chaque chiffre compte une puissance de 2 : ${exemple}.</p>`
            + `<p><b>Quelle est l'écriture décimale du nombre binaire ${bin} ?</b></p>`,
            { ...opts(L_BINAIRE), mode: 'saisie' });
        payload._meta.params = { sens, v, bin };
        return payload;
    }
    let v;
    do { v = randInt(5, 63); } while (v === 13);
    const bin = v.toString(2);
    const phrase = `$${v} = {{${bin}}}_{2}$`;
    const payload = makeTexteTrou(phrase,
        `<p>En binaire, chaque chiffre compte une puissance de 2 : ${exemple}.</p>`
        + `<p><b>Écris ${v} en binaire.</b> N'utilise que des 0 et des 1.</p>`,
        { ...opts(L_BINAIRE), mode: 'saisie' });
    payload._meta.params = { sens, v, bin };
    return payload;
};

// ═════════ « denombrement » — cadenas / containers ═════════

const genDenombrementTrous = () => {
    if (randInt(0, 1)) {
        // Code à n chiffres de 0 à 9 → 10^n possibilités.
        const n = randInt(2, 6);
        const N = pow(10, n);
        const objet = randomChoice([
            `Un cadenas possède ${n} molettes portant chacune les chiffres de 0 à 9.`,
            `Un coffre-fort s'ouvre avec un code de ${n} chiffres, chacun compris entre 0 et 9.`,
            `Un antivol de vélo comporte ${n} molettes portant chacune les chiffres de 0 à 9.`,
        ]);
        const phrase = `$10^{{{${n}}}} = {{${N}}}\\ \\text{codes possibles}$`;
        const payload = makeTexteTrou(phrase,
            `<p>${objet}</p>`
            + '<p><b>Écris le nombre de codes possibles en puissance de 10, puis en entier.</b> '
            + 'Écris l\'entier sans espaces.</p>',
            { ...opts(L_DENOMBREMENT), mode: 'saisie' });
        payload._meta.params = { famille: 'cadenas', n, N };
        return payload;
    }
    // Emboîtements par 8 = 2³ : L niveaux de 8, × 2^c paires (c ∈ 4..6).
    const L = randInt(2, 4);
    const c = randInt(4, 6);
    const paires = pow(2, c);
    const e = 3 * L + c;
    const total = pow(2, e);
    const NIVEAUX = [
        { n: 'containers', g: 'chacun' }, { n: 'caisses', g: 'chacune' },
        { n: 'cartons', g: 'chacun' }, { n: 'boîtes', g: 'chacune' },
    ];
    const niveaux = NIVEAUX.slice(0, L);
    let chaine = `8 ${niveaux[0].n}`;
    for (let i = 1; i < L; i++) chaine += `, ${niveaux[i - 1].g} contenant 8 ${niveaux[i].n}`;
    chaine += `, ${niveaux[L - 1].g} contenant ${paires} paires de chaussettes`;
    const phrase = `$${puissFrag(8, L)} \\times ${paires} = 2^{{{${e}}}} = {{${total}}}\\ \\text{paires}$`;
    const payload = makeTexteTrou(phrase,
        // Le « Rappel : 8 = 2³ et 64 = 2⁶ » livrait la décomposition à faire.
        `<p>Un entrepôt reçoit ${chaine}.</p>`
        + '<p><b>Écris le total en puissance de 2, puis en entier.</b> '
        + 'Écris l\'entier sans espaces.</p>',
        { ...opts(L_DENOMBREMENT), mode: 'saisie' });
    payload._meta.params = { famille: 'containers', L, c, e, total };
    return payload;
};

// ═════════ « balle » — rebonds aux 3/4 (hauteurs entières) ═════════

const genBalleTrous = () => {
    // H choisi multiple de q³ → h1, h2, h3 ENTIERS par construction.
    const FRACS = [
        { a: 3, b: 4, H: [192, 256, 320, 384, 448] },
        { a: 4, b: 5, H: [125, 250, 375, 500] },
        { a: 1, b: 2, H: [120, 200, 240, 320, 400] },
        { a: 2, b: 3, H: [135, 270, 351, 405] },
        { a: 3, b: 5, H: [250, 375, 500] },
    ];
    const { a, b, H: HS } = randomChoice(FRACS);
    const H = randomChoice(HS);
    const h1 = H * a / b; const h2 = h1 * a / b; const h3 = h2 * a / b;
    const phrase = `$h_{1} = {{${h1}}}\\ \\text{cm} \\qquad h_{2} = {{${h2}}}\\ \\text{cm}`
        + ` \\qquad h_{3} = H \\times \\left(${fracFrag(a, b)}\\right)^{{{3}}} = {{${h3}}}\\ \\text{cm}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On lâche une balle d'une hauteur H = ${H} cm. À chaque rebond, elle remonte aux `
        + `${tex(fracFrag(a, b))} de la hauteur d'où elle est tombée.</p>`
        // « l'exposant » seul était ambigu hors du gabarit de phrase.
        + '<p><b>Complète les hauteurs des trois premiers rebonds, puis l\'exposant de la puissance '
        + `de ${tex(fracFrag(a, b))} qui correspond au 3ᵉ rebond.</b></p>`,
        { ...opts(L_BALLE), mode: 'saisie' });
    payload._meta.params = { a, b, H, h1, h2, h3 };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    notation_texte_trou_saisie: genNotationTrous,
    calcul_qcm_unique: genCalculQcm,
    stockage_texte_trou_saisie: genStockageTrous,
    sciences_texte_trou_saisie: genSciencesTrous,
    bacteries_texte_trou_saisie: genBacteriesTrous,
    enigme_texte_trou_saisie: genEnigmeTrous,
    binaire_texte_trou_saisie: genBinaireTrous,
    denombrement_texte_trou_saisie: genDenombrementTrous,
    balle_texte_trou_saisie: genBalleTrous,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte_trou_saisie: 'Saisie trous',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'notation', label: L_NOTATION, formatsList: buildFormats('notation') },
    { id: 'calcul', label: L_CALCUL, formatsList: buildFormats('calcul') },
    { id: 'stockage', label: L_STOCKAGE, formatsList: buildFormats('stockage') },
    { id: 'sciences', label: L_SCIENCES, formatsList: buildFormats('sciences') },
    { id: 'bacteries', label: L_BACTERIES, formatsList: buildFormats('bacteries') },
    { id: 'enigme', label: L_ENIGME, formatsList: buildFormats('enigme') },
    { id: 'binaire', label: L_BINAIRE, formatsList: buildFormats('binaire') },
    { id: 'denombrement', label: L_DENOMBREMENT, formatsList: buildFormats('denombrement') },
    { id: 'balle', label: L_BALLE, formatsList: buildFormats('balle') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
